import { useMemo } from 'react';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';

import { FHAccordion, FHRadio, FHRadioGroup, FHTextarea } from '../../../base';
import { FORTA_BLUE } from '../../../../utils/colors';

import type { ChangeEvent } from 'react';
import type { IClinicalQAReview, IClinicalQAReviewDiscourse } from '../../../../utils/types';

interface CommentsSectionProps {
  reviewData: IClinicalQAReview;
  isEditable: boolean;
  disabled: boolean;
  onUpdate: (data: IClinicalQAReview) => void;
  handleSave: (data: IClinicalQAReview) => void;
}

function CommentsSection(props: CommentsSectionProps) {
  const { reviewData, isEditable, disabled, onUpdate, handleSave } = props;

  const latestComment = useMemo(() => {
    if (!reviewData?.discourses) return;

    return reviewData.discourses.find(
      (discourse: IClinicalQAReviewDiscourse) => !discourse.date_submitted,
    );
  }, [reviewData?.discourses]);

  const prevComments = useMemo(() => {
    const filtered = reviewData.discourses.filter((discourse: IClinicalQAReviewDiscourse) =>
      Boolean(discourse.date_submitted),
    );

    return filtered || [];
  }, [reviewData?.discourses]);

  const updateLatestComment = (key: 'is_edit_requested' | 'comment', value: boolean | string) =>
    reviewData.discourses.map((discourse: IClinicalQAReviewDiscourse) => {
      if (discourse.date_submitted) return discourse;

      if (key === 'is_edit_requested') {
        return { ...discourse, is_edit_requested: Boolean(value) };
      }

      return { ...discourse, comment: String(value) };
    });

  const handleUpdate = (key: 'is_edit_requested' | 'comment', value: boolean | string) => {
    const updatedData = {
      ...reviewData,
      discourses: updateLatestComment(key, value),
    };
    onUpdate(updatedData);
    // Do not allow saving in-progress reviews in its not an initial review
    if (key === 'is_edit_requested' && reviewData.discourses?.length < 2) {
      handleSave(updatedData);
    }
  };

  const handleSaveChanges = (key: 'is_edit_requested' | 'comment', value: boolean | string) => {
    // Do not allow saving in-progress reviews in its not an initial review
    if (reviewData.discourses?.length > 1) return;

    const updatedData = {
      ...reviewData,
      discourses: updateLatestComment(key, value),
    };
    handleSave(updatedData);
  };

  const CommentsViewOnly = () => {
    return (
      <>
        <Typography fontWeight={700} mb={1}>
          QA comments
        </Typography>
        {prevComments.map((discourse: IClinicalQAReviewDiscourse) => (
          <Box key={discourse.id} display={'flex'} flexDirection={'column'} mb={2}>
            <Typography fontWeight={700}>
              {moment(discourse.date_submitted).format('MMM Do')}
            </Typography>
            <Typography>{discourse.comment}</Typography>
          </Box>
        ))}
      </>
    );
  };

  return (
    <Box display={'flex'} flexDirection={'column'}>
      {isEditable ? (
        <>
          <Box mb={2}>
            <Typography fontWeight={700} mb={1}>
              Require edits to the session note?
            </Typography>
            <FHRadioGroup
              sx={{ flexDirection: 'row', columnGap: 3 }}
              coloredBackground
              value={
                latestComment?.is_edit_requested
                  ? 'Yes'
                  : latestComment?.is_edit_requested === null
                    ? ''
                    : 'No'
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleUpdate('is_edit_requested', e.target.value === 'Yes' ? true : false);
              }}
            >
              <FormControlLabel
                data-testid="editRequestedYesRadio"
                sx={{ width: 'fit-content' }}
                disabled={disabled}
                label="Yes"
                value="Yes"
                control={<FHRadio />}
              />
              <FormControlLabel
                data-testid="editRequestedNoRadio"
                sx={{ width: 'fit-content' }}
                disabled={disabled}
                label="No"
                value="No"
                control={<FHRadio />}
              />
            </FHRadioGroup>
          </Box>
          <Box display={'flex'} flexGrow={1} flexDirection={'column'}>
            <Typography fontWeight={700} mb={1}>
              Comment section
            </Typography>
            <FHTextarea
              data-testid="qaGradingCommentSection"
              sx={{
                height: '100%',
              }}
              disabled={disabled}
              fullHeight
              coloredBackground
              placeholder="Please, enter comments, observation or request edits for the session note"
              value={latestComment?.comment || ''}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleUpdate('comment', e.target.value)
              }
              onBlur={(e) => {
                handleSaveChanges('comment', e.target.value);
              }}
            />
            {prevComments.length > 0 && (
              <FHAccordion
                sx={{
                  mb: 0,
                  mt: 2,
                  backgroundColor: FORTA_BLUE.light3,
                  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                  '&.Mui-expanded': { mt: 2, '&:last-of-type': { mb: 1 } },
                }}
                defaultExpanded={false}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={700}>Previous comments</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {prevComments.map((discourse: IClinicalQAReviewDiscourse) => (
                    <Box key={discourse.id} display={'flex'} flexDirection={'column'} mb={2}>
                      <Typography fontWeight={700}>
                        {moment(discourse.date_submitted).format('MMM Do')}
                      </Typography>
                      <Typography>{discourse.comment}</Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </FHAccordion>
            )}
          </Box>
        </>
      ) : (
        <CommentsViewOnly />
      )}
    </Box>
  );
}

export default CommentsSection;
